/**
 * Locale utility functions for the catalog locale selector feature.
 *
 * Provides initial locale resolution with preference persistence
 * and human-readable locale label generation.
 */

/** Preference key used to store the user's selected catalog locale in domain-scoped user preferences. */
export const CATALOG_LOCALE_PREF_KEY = 'catalog_locale'

/** Minimal interface for reading/writing user preferences, compatible with the useUserPreferences hook. */
export interface UserPreferencesApi {
    get(path: string[]): any
    set(path: string[], value: any): void
}

/**
 * Resolves the initial locale to display on the catalog page.
 *
 * Resolution order:
 *   1. Saved user preference from domain-scoped user preferences (if still valid)
 *   2. Domain's configured default locale
 *   3. First entry in catalogLocales
 *
 * Stale preferences (no longer in catalogLocales) are soft-deleted automatically.
 *
 * @param catalogLocales - Available catalog locale codes (BCP 47), must be non-empty
 * @param catalogDefaultLocale - Domain's configured default locale (optional)
 * @param userPreferences - Domain-scoped user preferences API
 * @returns A locale string guaranteed to be a member of catalogLocales
 */
export function resolveInitialLocale(
    catalogLocales: string[],
    catalogDefaultLocale: string | undefined,
    userPreferences: UserPreferencesApi,
): string {
    // Step 1: Check saved user preference from domain-scoped preferences
    const saved = userPreferences.get([CATALOG_LOCALE_PREF_KEY]) as string | undefined

    if (saved && catalogLocales.includes(saved)) {
        return saved
    }

    // Soft-delete stale preference if locale was removed from config
    if (saved) {
        userPreferences.set([CATALOG_LOCALE_PREF_KEY], null)
    }

    // Step 2: Fall back to domain default or first catalog locale
    return catalogDefaultLocale ?? catalogLocales[0]
}

/**
 * Produces a human-readable label for a BCP 47 locale code.
 *
 * Uses Intl.Locale to parse the tag and Intl.DisplayNames to resolve
 * the language and region into display names. Shows the native language
 * name alongside the English name when they differ.
 *
 * Examples:
 *   "en-US"   → "English (United States)"
 *   "fr-CA"   → "French / Français (Canada)"
 *   "es-419"  → "Spanish / Español (Latin America)"
 *   "en"      → "English"
 *
 * @param locale - A BCP 47 locale string
 * @returns Formatted label, or the raw code on failure
 */
export function getLocaleLabel(locale: string): string {
    try {
        const parsed = new Intl.Locale(locale)
        const lang = parsed.language

        const englishLangNames = new Intl.DisplayNames(['en'], { type: 'language' })
        const englishName = englishLangNames.of(lang)
        if (!englishName) return locale

        // Get native name and include it if different from English
        const nativeLangNames = new Intl.DisplayNames([lang], { type: 'language' })
        const nativeName = nativeLangNames.of(lang)
        const showNative = nativeName && nativeName.toLowerCase() !== englishName.toLowerCase()

        const langLabel = showNative ? `${englishName} / ${nativeName}` : englishName

        if (parsed.region) {
            const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
            const region = regionNames.of(parsed.region)
            if (region) {
                return `${langLabel} (${region})`
            }
        }

        return langLabel
    } catch {
        return locale
    }
}
